/** Longest a redirect waits for LaunchDarkly to start sending analytics. */
export const LAUNCH_DARKLY_FLUSH_TIMEOUT_MS = 300;

/**
 * Sends buffered LaunchDarkly analytics before a hard navigation. A redirect
 * straight after a flag read otherwise unloads the page before the SDK sends,
 * so LaunchDarkly never records that evaluation (e.g. every `enable_dti_v3`
 * false served on the way to CHAS). The SDK posts with `keepalive`, so once the
 * send starts it survives navigation; the cap only bounds how long a slow or
 * blocked LaunchDarkly can delay the redirect. Never rejects.
 */
export async function flushLaunchDarklyEvents(
  flush: () => Promise<unknown>,
): Promise<void> {
  // Executor form on purpose: Next 16 targets Safari 16.4 / Chrome 111 and does
  // not polyfill Promise.withResolvers (Safari 17.4+ / Chrome 119+). Throwing
  // here would strand v2-bound visitors on the redirect screen.
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, LAUNCH_DARKLY_FLUSH_TIMEOUT_MS);
  });
  // Executor also turns a synchronous flush throw (e.g. closed client) into a
  // rejection, while still starting the send before this function returns.
  const flushed = new Promise<unknown>((resolve) => {
    resolve(flush());
  }).catch(() => undefined);
  await Promise.race([flushed, timeout]);
}

/**
 * Flushes, then navigates unless the returned cancel runs first. Call the
 * cancel from the cleanup of the effect that starts the redirect: the page
 * stays usable during the flush wait, so a redirect decided before the user
 * moved on (or before its inputs changed) must not fire afterwards. The cancel
 * returns whether it prevented the navigation (false once it has fired).
 */
export function flushThenNavigate(
  flush: () => Promise<unknown>,
  navigate: () => void,
): () => boolean {
  let settled = false;
  void flushLaunchDarklyEvents(flush).then(() => {
    if (settled) return;
    settled = true;
    navigate();
  });
  return () => {
    const prevented = !settled;
    settled = true;
    return prevented;
  };
}
